import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  BookOpen,
  Target,
  TrendingUp,
  Play,
  Brain,
  Users
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import procesoService from '../../services/procesoService';
import usuarioExamenService from '../../services/usuarioExamenService';
import usuarioProcesoService from '../../services/usuarioProcesoService';
import perfilPruebaService from '../../services/perfilPruebaService';
import examenService from '../../services/examenService';
import discService from '../../services/discService';
import ipvService from '../../services/ipvService';
import useAuthStore from '../../store/authStore';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loader from '../../components/ui/Loader';
import RealizarExamen from './RealizarExamen';
import RealizarExamenRaven from './RealizarExamenRaven';
import RealizarTestDisc from './RealizarTestDisc';
import RealizarExamenIpv from './RealizarExamenIpv';

const MisTests = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [procesos, setProcesos] = useState([]);
  const [examenActivo, setExamenActivo] = useState(null);
  const [testDiscActivo, setTestDiscActivo] = useState(null);
  const [examenIpvActivo, setExamenIpvActivo] = useState(null);
  const [estadosExamenes, setEstadosExamenes] = useState({});
  const [testsDiscDisponibles, setTestsDiscDisponibles] = useState([]);
  const [testsDiscCompletados, setTestsDiscCompletados] = useState([]);
  const [examenesIpvDisponibles, setExamenesIpvDisponibles] = useState([]);
  const [examenesIpvCompletados, setExamenesIpvCompletados] = useState([]);
  const [asociacionesPruebas, setAsociacionesPruebas] = useState([]);
  const [todosLosExamenes, setTodosLosExamenes] = useState([]);

  useEffect(() => {
    loadMisProcesos();
  }, []);

  const loadMisProcesos = async () => {
    try {
      setLoading(true);
      
      // Obtener los procesos asignados al usuario desde usuario-proceso
      const misAsignaciones = await usuarioProcesoService.getProcesosPorUsuario(user.id);
      
      // Obtener los IDs de los procesos asignados
      const procesosIds = misAsignaciones.map(up => up.procesoId);
      
      // Obtener todos los procesos
      const todosProcesos = await procesoService.getAllProcesos();
      
      // Filtrar solo los procesos asignados al usuario
      const misProcesos = todosProcesos.filter(p => procesosIds.includes(p.id));
      
      setProcesos(misProcesos);

      // Cargar todas las asociaciones de perfil-pruebas
      const asociaciones = await perfilPruebaService.getAll();
      setAsociacionesPruebas(asociaciones);

      // Cargar todos los exámenes Raven disponibles
      const examenes = await examenService.getAllExamenes();
      setTodosLosExamenes(examenes);

      // Cargar estados de todos los exámenes del usuario
      await cargarEstadosExamenes(misProcesos);

      // Cargar tests DISC disponibles para el usuario
      await cargarTestsDiscDisponibles();

      // Cargar tests DISC completados por el usuario
      await cargarTestsDiscCompletados();

      // Cargar exámenes IPV disponibles para el usuario
      await cargarExamenesIpvDisponibles();

      // Cargar exámenes IPV completados por el usuario
      await cargarExamenesIpvCompletados();
    } catch (error) {
      console.error('Error al cargar mis procesos:', error);
      if (error.response?.status !== 400) {
        toast.error('Error al cargar tus exámenes');
      }
      setProcesos([]);
    } finally {
      setLoading(false);
    }
  };

  const cargarEstadosExamenes = async (procesos) => {
    try {
      // Obtener todos los exámenes del usuario
      const todosLosExamenes = await usuarioExamenService.obtenerExamenesUsuario(user.id);
      
      // Crear un mapa de examenId -> estado
      const estadosMap = {};
      todosLosExamenes.forEach(examen => {
        estadosMap[examen.examenId] = {
          estado: examen.estado,
          puntajeObtenido: examen.puntajeObtenido,
          puntajeMaximo: examen.puntajeMaximo,
          porcentajeCompletado: examen.porcentajeCompletado
        };
      });
      
      setEstadosExamenes(estadosMap);
    } catch (error) {
      console.error('Error al cargar estados de exámenes:', error);
      // No mostrar error al usuario, solo log
    }
  };

  const cargarTestsDiscDisponibles = async () => {
    try {
      // Obtener todos los perfil-pruebas
      const todasLasPruebas = await perfilPruebaService.getAll();
      
      // Filtrar solo las pruebas DISC (que tienen discExamenId o testDiscId)
      const pruebasDisc = todasLasPruebas.filter(
        prueba => prueba.testDiscId != null || prueba.discExamenId != null
      );
      
      console.log('📊 Tests DISC disponibles:', pruebasDisc);
      setTestsDiscDisponibles(pruebasDisc);
    } catch (error) {
      console.error('Error al cargar tests DISC:', error);
      // No mostrar error al usuario
    }
  };

  const cargarTestsDiscCompletados = async () => {
    try {
      // Verificar si el usuario ya completó el test DISC
      const testsCompletados = await discService.getTestsCompletadosPorUsuario(user.id);
      console.log('✅ Tests DISC completados por el usuario:', testsCompletados);
      setTestsDiscCompletados(testsCompletados);
    } catch (error) {
      console.error('Error al cargar tests DISC completados:', error);
      // Si da error 404, significa que no ha completado ningún test
      setTestsDiscCompletados([]);
    }
  };

  const cargarExamenesIpvDisponibles = async () => {
    try {
      // Obtener todos los perfil-pruebas
      const todasLasPruebas = await perfilPruebaService.getAll();
      
      // Filtrar solo las pruebas IPV (que tienen ipvExamenId)
      const pruebasIpv = todasLasPruebas.filter(
        prueba => prueba.ipvExamenId != null
      );
      
      console.log('📊 Exámenes IPV disponibles:', pruebasIpv);
      setExamenesIpvDisponibles(pruebasIpv);
    } catch (error) {
      console.error('Error al cargar exámenes IPV:', error);
      // No mostrar error al usuario
    }
  };

  const cargarExamenesIpvCompletados = async () => {
    try {
      // Obtener evaluaciones IPV completadas por el usuario
      const evaluacionesData = await ipvService.getTodosLosExamenesCompletados();
      const evaluacionesUsuario = evaluacionesData.filter(ev => ev.usuarioId === user.id);
      console.log('✅ Exámenes IPV completados por el usuario:', evaluacionesUsuario);
      setExamenesIpvCompletados(evaluacionesUsuario);
    } catch (error) {
      console.error('Error al cargar exámenes IPV completados:', error);
      setExamenesIpvCompletados([]);
    }
  };

  const iniciarExamen = (examen, perfil, proceso) => {
    console.log('🎯 Iniciando examen:', {
      nombre: examen.nombre,
      tipoExamen: examen.tipoExamen,
      id: examen.id,
      preguntas: examen.preguntas?.length
    });
    
    setExamenActivo({
      examen,
      perfil,
      proceso
    });
  };

  const iniciarTestDisc = (proceso) => {
    setTestDiscActivo({
      proceso
    });
  };

  const iniciarExamenIpv = (proceso) => {
    setExamenIpvActivo({
      proceso
    });
  };

  const finalizarExamen = () => {
    setExamenActivo(null);
    loadMisProcesos(); // Recargar para actualizar estados
  };

  const finalizarTestDisc = () => {
    setTestDiscActivo(null);
    loadMisProcesos(); // Recargar para actualizar estados
  };

  const finalizarExamenIpv = () => {
    setExamenIpvActivo(null);
    loadMisProcesos(); // Recargar para actualizar estados
  };

  // Función helper para obtener exámenes Raven de un perfil usando tabla intermedia
  const getExamenesRavenPorPerfil = (perfilId) => {
    // Filtrar asociaciones que tienen examenRavenId y coinciden con el perfil
    const asociacionesPerfil = asociacionesPruebas.filter(
      a => a.perfilId === perfilId && a.examenRavenId != null
    );

    // Mapear las asociaciones a los exámenes completos
    const examenesPerfil = asociacionesPerfil
      .map(asociacion => {
        const examen = todosLosExamenes.find(e => e.id === asociacion.examenRavenId);
        return examen;
      })
      .filter(examen => examen != null); // Filtrar nulls

    return examenesPerfil;
  };

  if (testDiscActivo) {
    return (
      <RealizarTestDisc
        discExamen={null}
        proceso={testDiscActivo.proceso}
        onFinalizar={finalizarTestDisc}
      />
    );
  }

  if (examenIpvActivo) {
    return (
      <RealizarExamenIpv
        proceso={examenIpvActivo.proceso}
        onFinalizar={finalizarExamenIpv}
      />
    );
  }

  if (examenActivo) {
    // Detectar si es un examen tipo RAVEN para usar el componente específico
    const esExamenRaven = examenActivo.examen.tipoExamen === 'RAVEN';
    
    if (esExamenRaven) {
      console.log('🎯 Renderizando examen RAVEN con formato de lista');
      return (
        <RealizarExamenRaven
          examen={examenActivo.examen}
          perfil={examenActivo.perfil}
          proceso={examenActivo.proceso}
          onFinalizar={finalizarExamen}
        />
      );
    }
    
    console.log('📝 Renderizando examen tradicional pregunta por pregunta');
    return (
      <RealizarExamen
        examen={examenActivo.examen}
        perfil={examenActivo.perfil}
        proceso={examenActivo.proceso}
        onFinalizar={finalizarExamen}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader size="lg" />
      </div>
    );
  }

  const getTotalExamenes = () => {
    return procesos.reduce((total, proceso) => {
      return total + (proceso.perfiles?.reduce((sum, perfil) => {
        return sum + (perfil.examenes?.length || 0);
      }, 0) || 0);
    }, 0);
  };

  const getExamenesCompletados = () => {
    // Esto se debe implementar cuando tengas el endpoint de resultados
    return 0;
  };

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Exámenes</h1>
          <p className="text-neutral-600 mt-1">
            Realiza tus evaluaciones asignadas
          </p>
        </div>
      </div>

      

      {/* Lista de procesos y exámenes */}
      {procesos.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              No tienes exámenes asignados
            </h3>
            <p className="text-neutral-600">
              Contacta a tu supervisor para que te asigne un proceso
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {procesos.map((proceso) => (
            <Card key={proceso.id}>

              {/* Perfiles y Exámenes */}
              <div className="space-y-6">
                {proceso.perfiles && proceso.perfiles.length > 0 ? (
                  proceso.perfiles.map((perfil) => (
                    <div key={perfil.id} className="space-y-3">
                      {/* Header del Perfil */}
                      

                      {/* Grilla unificada de todos los exámenes */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Exámenes Raven del Perfil */}
                        {getExamenesRavenPorPerfil(perfil.id).map((examen) => (
                          <motion.div
                            key={`raven-${examen.id}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-gradient-to-br from-white to-neutral-50 border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h3 className="font-semibold text-neutral-900 mb-1">
                                  {examen.nombre}
                                </h3>
                                <p className="text-sm text-neutral-600 line-clamp-2">
                                  {examen.descripcion}
                                </p>
                              </div>
                            </div>

                            <div className="space-y-2 mb-4">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-neutral-600 flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  Duración:
                                </span>
                                <span className="font-medium text-neutral-900">
                                  {examen.tiempoMinutos} min
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-neutral-600 flex items-center gap-1">
                                  <FileText className="w-4 h-4" />
                                  Preguntas:
                                </span>
                                <span className="font-medium text-neutral-900">
                                  {examen.preguntas?.length || 0}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-neutral-600 flex items-center gap-1">
                                  <TrendingUp className="w-4 h-4" />
                                  Ponderación:
                                </span>
                                <span className="font-medium text-neutral-900">
                                  {examen.porcentajePonderacion}%
                                </span>
                              </div>
                              
                            </div>

                            <Button
                              variant="primary"
                              size="sm"
                              className="w-full"
                              onClick={() => {
                                // Marcar explícitamente como examen RAVEN
                                const examenRaven = { ...examen, tipoExamen: 'RAVEN' };
                                iniciarExamen(examenRaven, perfil, proceso);
                              }}
                              disabled={
                                !examen.preguntas || 
                                examen.preguntas.length === 0 || 
                                  estadosExamenes[examen.id]?.estado === 'FINALIZADO' ||
                                  estadosExamenes[examen.id]?.estado === 'COMPLETADO'
                                }
                              >
                                <Play className="w-4 h-4" />
                                {!examen.preguntas || examen.preguntas.length === 0 
                                  ? 'Sin preguntas' 
                                  : (estadosExamenes[examen.id]?.estado === 'FINALIZADO' || estadosExamenes[examen.id]?.estado === 'COMPLETADO')
                                  ? 'Examen Finalizado'
                                  : estadosExamenes[examen.id]?.estado === 'EN_PROGRESO'
                                  ? 'Continuar Examen'
                                  : 'Iniciar Examen'
                                }
                              </Button>
                            </motion.div>
                          ))}

                        {/* Tests DISC del Perfil */}
                        {testsDiscDisponibles
                          .filter(td => td.perfilId === perfil.id)
                          .map((testDisc) => (
                            <motion.div
                              key={`disc-${testDisc.id}`}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Brain className="w-5 h-5 text-purple-600" />
                                    <h3 className="font-semibold text-neutral-900">
                                      Test DISC
                                    </h3>
                                  </div>
                                  <p className="text-sm text-neutral-600 line-clamp-2">
                                    Evaluación de perfil de personalidad y comportamiento
                                  </p>
                                </div>
                              </div>

                              <div className="space-y-2 mb-4">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-neutral-600 flex items-center gap-1">
                                    <FileText className="w-4 h-4" />
                                    Grupos:
                                  </span>
                                  <span className="font-medium text-neutral-900">
                                    28 palabras
                                  </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-neutral-600 flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    Duración:
                                  </span>
                                  <span className="font-medium text-neutral-900">
                                    10-15 min
                                  </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-neutral-600">Perfil:</span>
                                  <Badge variant="secondary">{testDisc.perfilNombre}</Badge>
                                </div>
                                {testsDiscCompletados.length > 0 && (
                                  <div className="pt-2 border-t border-purple-200">
                                    <Badge variant="success" className="w-full justify-center">
                                      <CheckCircle className="w-4 h-4" />
                                      Test Completado
                                    </Badge>
                                  </div>
                                )}
                              </div>

                              <Button
                                variant={testsDiscCompletados.length > 0 ? "outline" : "primary"}
                                size="sm"
                                className={testsDiscCompletados.length > 0 
                                  ? "w-full" 
                                  : "w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                                }
                                onClick={() => iniciarTestDisc(proceso)}
                                disabled={testsDiscCompletados.length > 0}
                              >
                                <Brain className="w-4 h-4" />
                                {testsDiscCompletados.length > 0 ? 'Test Finalizado' : 'Iniciar Test DISC'}
                              </Button>
                            </motion.div>
                          ))}

                        {/* Exámenes IPV del Perfil */}
                        {examenesIpvDisponibles
                          .filter(ipv => ipv.perfilId === perfil.id)
                          .map((examenIpv) => (
                            <motion.div
                              key={`ipv-${examenIpv.id}`}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Users className="w-5 h-5 text-indigo-600" />
                                    <h3 className="font-semibold text-neutral-900">
                                      Examen IPV
                                    </h3>
                                  </div>
                                  <p className="text-sm text-neutral-600 line-clamp-2">
                                    Evaluación de características de personalidad para ventas
                                  </p>
                                </div>
                              </div>

                              <div className="space-y-2 mb-4">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-neutral-600 flex items-center gap-1">
                                    <FileText className="w-4 h-4" />
                                    Preguntas:
                                  </span>
                                  <span className="font-medium text-neutral-900">
                                    87 ítems
                                  </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-neutral-600 flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    Duración:
                                  </span>
                                  <span className="font-medium text-neutral-900">
                                    15-20 min
                                  </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-neutral-600">Perfil:</span>
                                  <Badge variant="secondary">{examenIpv.perfilNombre}</Badge>
                                </div>
                                {examenesIpvCompletados.length > 0 && (
                                  <div className="pt-2 border-t border-indigo-200">
                                    <Badge variant="success" className="w-full justify-center">
                                      <CheckCircle className="w-4 h-4" />
                                      Examen Completado
                                    </Badge>
                                  </div>
                                )}
                              </div>

                              <Button
                                variant={examenesIpvCompletados.length > 0 ? "outline" : "primary"}
                                size="sm"
                                className={examenesIpvCompletados.length > 0 
                                  ? "w-full" 
                                  : "w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                                }
                                onClick={() => iniciarExamenIpv(proceso)}
                                disabled={examenesIpvCompletados.length > 0}
                              >
                                <Users className="w-4 h-4" />
                                {examenesIpvCompletados.length > 0 ? 'Examen Finalizado' : 'Iniciar Examen IPV'}
                              </Button>
                            </motion.div>
                          ))}
                      </div>

                      {/* Mensaje si no hay exámenes ni tests DISC ni IPV */}
                      {getExamenesRavenPorPerfil(perfil.id).length === 0 && 
                       testsDiscDisponibles.filter(td => td.perfilId === perfil.id).length === 0 &&
                       examenesIpvDisponibles.filter(ipv => ipv.perfilId === perfil.id).length === 0 && (
                        <div className="text-center py-8 bg-neutral-50 rounded-lg">
                          <FileText className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
                          <p className="text-neutral-600">
                            No hay pruebas disponibles en este perfil
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 bg-neutral-50 rounded-lg">
                    <BookOpen className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
                    <p className="text-neutral-600">
                      Este proceso no tiene perfiles asignados
                    </p>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MisTests;
